/**
 * Parser for the canonical design-pattern XML skeleton.
 *
 * Design-pattern (and some framework) docs embed a small, fixed set of
 * structural tags inside otherwise-plain markdown:
 *
 *   <rules><rule id="…" priority="must">…</rule>…</rules>
 *   <applies_when>…</applies_when>
 *   <avoid><mistake>…</mistake>…</avoid>
 *   <verify><check>…</check>…</verify>
 *   <notes>…</notes>
 *
 * `parsePatternDoc` segments a frontmatter-stripped doc body into an
 * ordered list of `PatternSegment`s: raw-markdown runs, and recognized
 * structural blocks. It is a straight-line scanner over this one fixed tag
 * set — not a general XML/HTML parser — and it is total: malformed,
 * unclosed, or unrecognized tags always degrade to a markdown segment
 * rather than throwing.
 */

export const RULE_PRIORITIES = ['must', 'should', 'may'] as const;
type ContainerTag = 'rules' | 'applies_when' | 'avoid' | 'verify' | 'notes';

export type PatternSegment =
  | { body: string; kind: 'markdown' }
  | { kind: 'rules'; rules: PatternRule[] }
  | { body: string; kind: 'applies_when' }
  | { kind: 'avoid'; mistakes: string[] }
  | { checks: string[]; kind: 'verify' }
  | { body: string; kind: 'notes' };

export type RulePriority = (typeof RULE_PRIORITIES)[number] | 'unknown';

export interface PatternRule {
  id: string | null;
  /** Normalized priority bucket — always one of RULE_PRIORITIES or 'unknown'. */
  priority: RulePriority;
  /** Original attribute value, preserved for labeling an 'unknown' badge. */
  rawPriority: string | null;
  /** Inline markdown body of the rule. */
  body: string;
}

const CONTAINER_TAG_RE = /^<(rules|applies_when|avoid|verify|notes)>$/;
const FENCE_RE = /^(`{3,}|~{3,})/;

interface ChildBlock {
  openLine: string;
  bodyLines: string[];
}

/** Tracks whether the scanner is currently inside a fenced code block. */
interface FenceState {
  inFence: boolean;
  fenceChar: string;
  fenceLen: number;
}

function buildContainerSegment(tagName: ContainerTag, innerLines: string[]): PatternSegment | null {
  switch (tagName) {
    case 'applies_when':
      return { body: innerLines.join('\n').trim(), kind: 'applies_when' };
    case 'notes':
      return { body: innerLines.join('\n').trim(), kind: 'notes' };
    case 'rules': {
      const { children, valid } = extractChildren(innerLines, 'rule');
      if (!valid) return null;
      return { kind: 'rules', rules: children.map(buildRule) };
    }
    case 'avoid': {
      const { children, valid } = extractChildren(innerLines, 'mistake');
      if (!valid) return null;
      return {
        kind: 'avoid',
        mistakes: children.map(c => c.bodyLines.join('\n').trim()),
      };
    }
    case 'verify': {
      const { children, valid } = extractChildren(innerLines, 'check');
      if (!valid) return null;
      return {
        checks: children.map(c => c.bodyLines.join('\n').trim()),
        kind: 'verify',
      };
    }
  }
}

function buildRule(child: ChildBlock): PatternRule {
  const idMatch = /\bid=["']([^"']*)["']/.exec(child.openLine);
  const priorityMatch = /\bpriority=["']([^"']*)["']/.exec(child.openLine);
  const rawPriority = priorityMatch ? priorityMatch[1] : null;

  return {
    body: child.bodyLines.join('\n').trim(),
    id: idMatch ? idMatch[1] : null,
    priority: normalizePriority(rawPriority),
    rawPriority,
  };
}

function createFenceState(): FenceState {
  return { fenceChar: '', fenceLen: 0, inFence: false };
}

/**
 * Scan `lines` (the inner content of a rules/avoid/verify container) for
 * repeated `<childTag …>…</childTag>` sequences.
 *
 * `valid` is true only when the container contains at least one child AND
 * no non-whitespace text/unclosed tags exist outside recognized children —
 * this is the "container fallback" contract: bare prose inside a container
 * (a shape framework docs use) must never be silently dropped.
 */
function extractChildren(
  lines: string[],
  childTag: string,
): { children: ChildBlock[]; valid: boolean } {
  const children: ChildBlock[] = [];
  const openRe = childTag === 'rule' ? /^<rule\b[^>]*>$/ : new RegExp(`^<${childTag}>$`);
  const fence = createFenceState();
  let sawStray = false;

  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (toggleFence(fence, trimmed)) {
      if (trimmed !== '') sawStray = true;
      i++;
      continue;
    }

    if (!fence.inFence && openRe.test(trimmed)) {
      const closeIdx = findMatchingClose(lines, i + 1, childTag, openRe);
      if (closeIdx === -1) {
        // Unclosed child tag — content is malformed; the whole container falls back.
        sawStray = true;
        i++;
        continue;
      }
      children.push({ bodyLines: lines.slice(i + 1, closeIdx), openLine: trimmed });
      i = closeIdx + 1;
      continue;
    }

    if (!fence.inFence && trimmed !== '') {
      sawStray = true;
    }
    i++;
  }

  return { children, valid: children.length > 0 && !sawStray };
}

/**
 * Find the line index of the standalone `</tagName>` close tag matching an
 * open tag whose content starts at `fromIndex`. Skips fenced code blocks.
 * Returns -1 when no matching close tag exists (malformed/unclosed).
 *
 * When `reopenRe` is provided, encountering another standalone line matching
 * it before the close tag also counts as "no matching close" — same-named
 * tags never legitimately nest in this schema, so a reopen means the first
 * occurrence was left unclosed.
 */
function findMatchingClose(
  lines: string[],
  fromIndex: number,
  tagName: string,
  reopenRe?: RegExp,
): number {
  const closeTag = `</${tagName}>`;
  const fence = createFenceState();

  for (let i = fromIndex; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (toggleFence(fence, trimmed)) continue;
    if (fence.inFence) continue;
    if (trimmed === closeTag) return i;
    if (reopenRe && reopenRe.test(trimmed)) return -1;
  }
  return -1;
}

/**
 * Update fence state for a line, if it opens/closes a fenced code block.
 * Returns true when the line was a fence marker (caller should not scan it
 * for structural tags).
 */
function toggleFence(state: FenceState, trimmedLine: string): boolean {
  const match = FENCE_RE.exec(trimmedLine);
  if (!match) return false;

  const marker = match[1];
  if (!state.inFence) {
    state.inFence = true;
    state.fenceChar = marker[0];
    state.fenceLen = marker.length;
  } else if (marker.startsWith(state.fenceChar) && marker.length >= state.fenceLen) {
    state.inFence = false;
    state.fenceChar = '';
    state.fenceLen = 0;
  }
  return true;
}

/** Normalize a raw `priority` attribute value into a RulePriority bucket. */
export function normalizePriority(raw: string | null): RulePriority {
  if (raw === null) return 'unknown';
  const lower = raw.trim().toLowerCase();
  return (RULE_PRIORITIES as readonly string[]).includes(lower)
    ? (lower as RulePriority)
    : 'unknown';
}

/**
 * Segment a frontmatter-stripped pattern-doc body into an ordered list of
 * markdown runs and recognized structural blocks. Preserves original
 * document order. Never throws — malformed or unrecognized tags degrade to
 * markdown segments.
 */
export function parsePatternDoc(body: string): PatternSegment[] {
  const lines = body.split(/\r?\n/);
  const segments: PatternSegment[] = [];
  const fence = createFenceState();
  let buffer: string[] = [];

  const flushMarkdown = (): void => {
    if (buffer.length === 0) return;
    const text = buffer.join('\n');
    if (text.trim() !== '') {
      segments.push({ body: text, kind: 'markdown' });
    }
    buffer = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (toggleFence(fence, trimmed)) {
      buffer.push(line);
      i++;
      continue;
    }

    if (!fence.inFence) {
      const containerMatch = CONTAINER_TAG_RE.exec(trimmed);
      if (containerMatch) {
        const tagName = containerMatch[1] as ContainerTag;
        const closeIdx = findMatchingClose(lines, i + 1, tagName);
        if (closeIdx !== -1) {
          const innerLines = lines.slice(i + 1, closeIdx);
          const built = buildContainerSegment(tagName, innerLines);
          if (built) {
            flushMarkdown();
            segments.push(built);
          } else {
            // Container fallback: preserve the whole block, wrappers and all,
            // as raw markdown (stripXmlTags removes the wrapper tags downstream).
            buffer.push(...lines.slice(i, closeIdx + 1));
          }
          i = closeIdx + 1;
          continue;
        }
        // Unclosed container tag — malformed; fall through and treat this
        // line as ordinary markdown text. Never throw.
      }
    }

    buffer.push(line);
    i++;
  }

  flushMarkdown();
  return segments;
}
